import { useState, useMemo, useEffect } from 'react';
import { toast, ToastContainer } from "react-toastify";
import { FaChevronRight } from 'react-icons/fa';
import TanStackTable from '../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import ActiveStatus from '../../components/Common/ActiveStatus';
import { useHelpRequests } from '../../queries/helpDesk';
import { useNavigate } from 'react-router-dom';

// Mock data for issues (replace with real API/hook as needed)
// const mockIssues = Array.from({ length: 100 }, (_, i) => ({
//   _id: i + 1,
//   icon: 'https://randomuser.me/api/portraits/men/1.jpg',
//   userName: 'John Doe',
//   helpDeskIssue: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
//   issueCategory: 'Issue Category',
//   problem: 'Not able to upload the image',
//   status: true,
// }));

const IssueList = () => {
  const navigate = useNavigate();
  // Pagination state
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    username: '',
    concern: '',
  });
  const { data: issues, isLoading, isError } = useHelpRequests()
  const [filteredData, setFilteredData] = useState(issues || []);

  // Update filteredData when issues change
  useEffect(() => {
    setFilteredData(issues || []);
  }, [issues]);

  // Filtering logic
  useEffect(() => {
    let filtered = issues || [];
    if (search) {
      filtered = filtered.filter(issue =>
        issue.createdBy.username.toLowerCase().includes(search.toLowerCase()) ||
        issue.concern.toLowerCase().includes(search.toLowerCase()) ||
        issue.title.toLowerCase().includes(search.toLowerCase()) ||
        issue.description.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (filters.username) {
      filtered = filtered.filter(issue =>
        issue.createdBy.username.toLowerCase().includes(filters.username.toLowerCase())
      );
    }
    if (filters.concern) {
      filtered = filtered.filter(issue =>
        issue.concern.toLowerCase().includes(filters.concern.toLowerCase())
      );
    }
    if (filters.status) {
      filtered = filtered.filter(issue =>
        issue.status === filters.status
      );
    }
    setFilteredData(filtered);
    setPage(1); // Reset to first page on filter
  }, [search, filters, issues]);

  // Status toggle handler
  const handleStatusToggle = (id) => {
    setFilteredData(prev =>
      prev.map(issue =>
        issue._id === id ? {
          ...issue,
          status: issue.status === 'active' ? 'resolved' : 'active'
        } : issue
      )
    );
    toast.success('Status updated!');
  };

  // Table columns
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.display({
      id: 'srno',
      header: 'Sr. No.',
      cell: info => (page - 1) * pageSize + info.row.index + 1,
    }),
    columnHelper.display({
      id: 'icons',
      header: 'Icons',
      cell: info => (
        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
          <span className="text-xs font-semibold text-gray-600">
            {info.row.original.createdBy.username.charAt(0).toUpperCase()}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor('createdBy.username', {
      header: 'User Name',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('title', {
      header: 'Help Desk - Issue',
      cell: info => (
        <div className="max-w-xs truncate" title={info.getValue()}>{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor('concern', {
      header: 'Issue Category',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('description', {
      header: 'Problem',
      cell: info => (
        <div className="max-w-xs truncate" title={info.getValue()}>{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      align: 'center',
      cell: info => {
        console.log(info.row.original);
        return <ActiveStatus
          active={info.getValue() === 'active' ? true : false}
          setActive={() => {}}
          label=" "
          innerActiveText="Active"
          innerInactiveText="Resolved"
        />
      },
    }),
    // columnHelper.display({
    //   id: 'status',
    //   header: 'Status',
    //   align: 'center',
    //   cell: info => (
    //     <div className="flex items-center gap-2">
    //       <ActiveStatus
    //         active={info.row.original.status === 'active'}
    //         setActive={() => handleStatusToggle(info.row.original._id)}
    //         label=" "
    //         innerActiveText="Active"
    //         innerInactiveText="Resolved"
    //       />
    //     </div>
    //   ),
    // }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      align: 'center',
      cell: info => (
        <button className="bg-[#F4F8FB] rounded p-2 hover:bg-[#e0e7ef]" title="View Details">
          <FaChevronRight className="text-[#B5B5C3]" onClick={
            () => navigate(`/help-desk/user-chat/${info.row.original._id}`)
          } />
        </button>
      ),
    }),
  ], [page]);

  // Pagination logic
  const paginatedData = filteredData.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(filteredData.length / pageSize);

  if (isLoading) {
    return (
      <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
        <h1 className="text-2xl font-bold text-[#2ECC40] mb-2">Help Desk- Issue List</h1>
        <div className="bg-white rounded-md shadow p-4 mb-4 text-center">
          <div className="text-gray-500">Loading issues...</div>
        </div>
      </main>
    );
  }

  if (isError) {
    return (
      <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
        <h1 className="text-2xl font-bold text-[#2ECC40] mb-2">Help Desk- Issue List</h1>
        <div className="bg-white rounded-md shadow p-4 mb-4 text-center">
          <div className="text-red-500">Error loading issues. Please try again.</div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
      {/* Header */}
      <h1 className="text-2xl font-bold text-[#2ECC40] mb-2">Help Desk- Issue List</h1>

      {/* Search Bar */}
      {/* <div className="bg-white rounded-md shadow p-4 mb-4">
        <input
          type="text"
          placeholder="Search by username, concern, title, or description..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full border border-gray-200 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-200"
        />
      </div> */}

      {/* Filters */}
      {/* <form className="bg-white rounded-md shadow p-4 mb-4" onSubmit={e => { e.preventDefault(); }}>
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm mb-1">User Name</label>
            <input
              type="text"
              name="username"
              value={filters.username}
              onChange={e => setFilters(f => ({ ...f, username: e.target.value }))}
              placeholder="Enter User Name"
              className="w-full border border-gray-200 rounded-md px-3 py-2"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm mb-1">Issue Category</label>
            <input
              type="text"
              name="concern"
              value={filters.concern}
              onChange={e => setFilters(f => ({ ...f, concern: e.target.value }))}
              placeholder="Enter Issue Category"
              className="w-full border border-gray-200 rounded-md px-3 py-2"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm mb-1">Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              className="w-full border border-gray-200 rounded-md px-3 py-2 bg-white"
            >
              <option value="active">Active</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md border border-gray-200"
              onClick={() => {
                setFilters({ username: '', concern: '', status: 'active' });
                setSearch('');
              }}
            >
              Reset
            </button>
            <button
              type="submit"
              className="bg-[#2ECC40] text-white px-6 py-2 rounded-md hover:bg-green-600"
            >
              Search
            </button>
          </div>
        </div>
      </form> */}

      {/* Table Section */}
      <section className="bg-white rounded-md shadow p-4 mb-4">
        <div className="mb-2 font-semibold">Issue List</div>
        <TanStackTable
          data={paginatedData}
          columns={columns}
          enablePagination={true}
        />
        {/* Pagination and count */}
        {/* <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500">
            {filteredData.length > 0 ? (page - 1) * pageSize + 1 : 0} out of {filteredData.length}
          </div>
          {totalPages > 1 && (
            <div className="flex gap-1">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`px-3 py-1 rounded ${page === i + 1 ? 'bg-[#2ECC40] text-white' : 'bg-gray-100 text-gray-700'}`}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div> */}
      </section>
      <ToastContainer position="top-right" autoClose={2000} />
    </main>
  );
};

export default IssueList;